import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import TextInput from "@components/textInput";
import { useModals } from "@contexts/ModalsContext";
import * as apiAdministration from "@api/administration";
import styles from "./SelectUser.module.css";

const DEFAULT_PAGE = 1;

const formatUserLabel = (user) => {
  if (!user) {
    return "";
  }

  const name = [user.name, user.lastName].filter(Boolean).join(" ").trim();
  const email = user.email ? ` • ${user.email}` : "";
  return `${name || "Utente"} (#${user.id})${email}`;
};

const UserSearchModal = ({
  initialValue,
  onSelect,
  onSelectId,
  onClose,
}) => {
  const [filter, setFilter] = useState(initialValue ?? "");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadUsers = useCallback((searchValue) => {
    if (!searchValue) {
      setUsers([]);
      return;
    }

    setLoading(true);
    apiAdministration
      .GetUsers({ page: DEFAULT_PAGE, filter: searchValue })
      .then((response) => setUsers(response ?? []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const trimmedFilter = filter.trim();
    if (!trimmedFilter) {
      setUsers([]);
      return;
    }

    const handler = setTimeout(() => {
      loadUsers(trimmedFilter);
    }, 300);

    return () => clearTimeout(handler);
  }, [filter, loadUsers]);

  const onEnterPress = useCallback(() => {
    const trimmed = filter.trim();
    if (Number.isInteger(Number(trimmed))) {
      onSelectId(trimmed);
    }
  }, [filter, onSelectId]);

  const listItems = useMemo(() => {
    if (loading) {
      return (
        <div className={styles.listState}>Caricamento...</div>
      );
    }

    if (users.length === 0) {
      return (
        <div className={styles.listState}>Nessun utente trovato</div>
      );
    }

    return users.map((user) => (
      <button
        type="button"
        key={user.id}
        className={styles.listItem}
        onClick={() => onSelect(user)}
      >
        <span className={styles.userLabel}>{formatUserLabel(user)}</span>
      </button>
    ));
  }, [loading, onSelect, users]);

  return (
    <div className={styles.modalContent}>
      <TextInput
        label="Cerca utente"
        value={filter}
        setValue={setFilter}
        onKeyPressEnter={onEnterPress}
        placeholder="Cerca utente per nome, email o ID"
      />
      <div className={styles.modalList}>
        {listItems}
      </div>
      <button type="button" className={styles.modalClose} onClick={onClose}>
        Chiudi
      </button>
    </div>
  );
};

UserSearchModal.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onSelectId: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

UserSearchModal.defaultProps = {
  initialValue: "",
};

const SelectUser = ({ label, value, setValue, disabled }) => {
  const { openModal, closeModal, activeModals } = useModals();
  const [displayValue, setDisplayValue] = useState(value ? value.toString() : "");
  const [selectedUser, setSelectedUser] = useState(null);
  const modalIdRef = useRef(null);

  const loadSelectedUser = useCallback((searchValue) => {
    if (!searchValue) {
      return;
    }

    apiAdministration
      .GetUsers({ page: DEFAULT_PAGE, filter: searchValue })
      .then((response) => {
        const matched = (response ?? []).find(
          (user) => String(user.id) === String(searchValue)
        );
        if (matched) {
          setSelectedUser(matched);
          setDisplayValue(formatUserLabel(matched));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!value) {
      setSelectedUser(null);
      setDisplayValue("");
      return;
    }

    if (selectedUser && String(selectedUser.id) === String(value)) {
      setDisplayValue(formatUserLabel(selectedUser));
      return;
    }

    const nextValue = value.toString();
    setDisplayValue(nextValue);
    loadSelectedUser(nextValue);
  }, [loadSelectedUser, selectedUser, value]);

  useEffect(() => {
    if (!modalIdRef.current) {
      return;
    }

    const stillOpen = activeModals.some((modal) => modal.id === modalIdRef.current);
    if (!stillOpen) {
      modalIdRef.current = null;
    }
  }, [activeModals]);

  const openSearchModal = useCallback((initialValue) => {
    if (disabled || modalIdRef.current) {
      return;
    }

    let modalId = null;

    const handleSelect = (user) => {
      setSelectedUser(user);
      setValue(user.id?.toString() || "");
      setDisplayValue(formatUserLabel(user));
      closeModal(modalId);
    };

    const handleSelectId = (id) => {
      setSelectedUser(null);
      setValue(id);
      setDisplayValue(id.toString());
      closeModal(modalId);
    };

    const handleClose = () => {
      closeModal(modalId);
    };

    modalId = openModal({
      title: "Cerca utente",
      children: (
        <UserSearchModal
          initialValue={initialValue}
          onSelect={handleSelect}
          onSelectId={handleSelectId}
          onClose={handleClose}
        />
      ),
    });

    modalIdRef.current = modalId;
  }, [closeModal, disabled, openModal, setValue]);

  const onInputFocus = useCallback(() => {
    openSearchModal(displayValue);
  }, [displayValue, openSearchModal]);

  const onInputChange = useCallback((nextValue) => {
    setDisplayValue(nextValue);
    openSearchModal(nextValue);
  }, [openSearchModal]);

  return (
    <div className={styles.wrapper}>
      <TextInput
        label={label}
        value={displayValue}
        setValue={onInputChange}
        disabled={disabled}
        onFocus={onInputFocus}
        placeholder="Cerca utente per nome, email o ID"
      />
    </div>
  );
};

SelectUser.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SelectUser.defaultProps = {
  label: "Utente",
  value: "",
  disabled: false,
};

export default SelectUser;
